import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send, ChevronLeft, ChevronRight, Bot, User, X } from "lucide-react";
import { api } from "../../services/api";
import MarkdownMessage from "./MarkdownMessage";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface AiChatWidgetProps {
  context?: { page?: string };
}

const STORAGE_KEY = "nuance-ai-panel-width";
const POSITION_KEY = "nuance-ai-tab-bottom";
const DEFAULT_WIDTH = 380;
const MIN_WIDTH = 320;
const MAX_WIDTH = 600;
// Default bottom offset aligns with sidebar Collapse button (~140px from bottom)
const DEFAULT_BOTTOM = 140;
const MIN_BOTTOM = 20;

function getStoredWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const w = parseInt(stored, 10);
      if (w >= MIN_WIDTH && w <= MAX_WIDTH) return w;
    }
  } catch {}
  return DEFAULT_WIDTH;
}

function getStoredBottom(): number {
  try {
    const stored = localStorage.getItem(POSITION_KEY);
    if (stored) {
      const b = parseInt(stored, 10);
      if (b >= MIN_BOTTOM) return b;
    }
  } catch {}
  return DEFAULT_BOTTOM;
}

export function AiChatWidget({ context }: AiChatWidgetProps) {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(getStoredWidth);
  const [bottomOffset, setBottomOffset] = useState(getStoredBottom);
  const isDraggingTab = useRef(false);
  const didDragTab = useRef(false);
  const dragStartY = useRef(0);
  const dragStartBottom = useRef(0);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      content:
        "Hi! I'm the Forge AI assistant. Ask me about pipeline status, data quality, column mappings, or anything else about your data.",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Persist width to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(width));
    } catch {}
  }, [width]);

  // Persist tab position to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(POSITION_KEY, String(bottomOffset));
    } catch {}
  }, [bottomOffset]);

  // Drag-to-reposition tab vertically
  const handleTabDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDraggingTab.current = true;
      didDragTab.current = false;
      dragStartY.current = e.clientY;
      dragStartBottom.current = bottomOffset;
      document.body.style.cursor = "grabbing";
      document.body.style.userSelect = "none";

      const handleMouseMove = (ev: MouseEvent) => {
        if (!isDraggingTab.current) return;
        const deltaY = dragStartY.current - ev.clientY;
        if (Math.abs(deltaY) > 3) didDragTab.current = true; // threshold to distinguish click vs drag
        const maxBottom = window.innerHeight - 100;
        const newBottom = Math.min(
          maxBottom,
          Math.max(MIN_BOTTOM, dragStartBottom.current + deltaY),
        );
        setBottomOffset(newBottom);
      };

      const handleMouseUp = () => {
        isDraggingTab.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [bottomOffset],
  );

  // Drag-to-resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, newWidth)));
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const data = await api.post<{ response: string }>("/manage/ai/chat", {
        message: userMessage.content,
        context: context ?? {},
      });

      setMessages((prev) => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          role: "assistant",
          content: data.response,
          timestamp: new Date(),
        },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: "assistant",
          content:
            e instanceof Error && e.message.includes("503")
              ? "AI features are not configured yet. Set CLAUDE_API_KEY in Settings to enable."
              : `Sorry, I encountered an error: ${e instanceof Error ? e.message : "Unknown error"}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Collapsed: vertical edge tab on right side, draggable up/down */}
      {!open && (
        <button
          onClick={() => {
            // Only open if we didn't just finish a drag
            if (!didDragTab.current) setOpen(true);
          }}
          onMouseDown={handleTabDragStart}
          className="fixed right-0 z-40 flex items-center gap-1.5 px-1.5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-l-lg shadow-lg transition-colors cursor-grab active:cursor-grabbing"
          style={{ bottom: `${bottomOffset}px` }}
          title="Open AI Assistant — drag to reposition"
        >
          <Bot className="w-4 h-4" />
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Expanded: right-side fixed panel */}
      <div
        className="fixed top-16 bottom-0 z-40 flex transition-transform duration-300 ease-in-out"
        style={{
          width: `${width}px`,
          right: 0,
          transform: open ? "translateX(0)" : `translateX(100%)`,
        }}
      >
        {/* Resize handle */}
        <div
          onMouseDown={handleResizeStart}
          className="w-1.5 cursor-col-resize bg-gray-200 dark:bg-gray-700 hover:bg-blue-400 dark:hover:bg-blue-500 transition-colors flex-shrink-0"
          title="Drag to resize"
        />

        {/* Panel content */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 bg-blue-600 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-white" />
              <span className="text-sm font-medium text-white">
                Forge AI Assistant
              </span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 text-white/80 hover:text-white hover:bg-white/10 rounded transition-colors"
              title="Close AI Assistant"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "assistant" && (
                  <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === "user"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  }`}
                >
                  {msg.role === "assistant" ? (
                    <MarkdownMessage content={msg.content} />
                  ) : (
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  )}
                </div>
                {msg.role === "user" && (
                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3.5 h-3.5 text-blue-600 animate-pulse" />
                </div>
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2">
                  <div className="flex gap-1">
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    />
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    />
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your pipelines..."
                disabled={loading}
                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
